import React, { useState } from 'react';
import { Mail, Phone, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { API } from '../App';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_interest: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await API.post('/contact', formData);
      setSubmitted(true);
      toast.success('Message sent! I\'ll be in touch soon.');
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#2C5530]" data-testid="contact-hero">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-white/70 text-sm uppercase tracking-[0.2em] mb-4">Contact</p>
            <h1 className="font-['Playfair_Display'] text-4xl md:text-6xl text-white font-medium mb-6">
              Let's Start a Conversation
            </h1>
            <p className="text-white/80 text-xl leading-relaxed">
              Whether you have questions or are ready to begin your journey to a quieter mind, I'd love to hear from you.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32" data-testid="contact-content">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Playfair_Display'] text-3xl text-[#1A1A1A] mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-6 mb-12">
                <a
                  href="mailto:scott@quietmindgolf.co.uk"
                  className="flex items-start gap-4 p-4 bg-white border border-[#E7E5E4] hover:border-[#2C5530]/30 transition-colors"
                  data-testid="contact-email"
                >
                  <Mail className="w-6 h-6 text-[#2C5530] mt-1" />
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Email</p>
                    <p className="text-[#57534E]">scott@quietmindgolf.co.uk</p>
                  </div>
                </a>

                <a
                  href="tel:+447455558064"
                  className="flex items-start gap-4 p-4 bg-white border border-[#E7E5E4] hover:border-[#2C5530]/30 transition-colors"
                  data-testid="contact-phone-uk"
                >
                  <Phone className="w-6 h-6 text-[#2C5530] mt-1" />
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Phone (UK)</p>
                    <p className="text-[#57534E]">+44 7455 558064</p>
                  </div>
                </a>

                <a
                  href="tel:+64274108444"
                  className="flex items-start gap-4 p-4 bg-white border border-[#E7E5E4] hover:border-[#2C5530]/30 transition-colors"
                  data-testid="contact-phone-nz"
                >
                  <Phone className="w-6 h-6 text-[#2C5530] mt-1" />
                  <div>
                    <p className="font-semibold text-[#1A1A1A]">Phone (NZ)</p>
                    <p className="text-[#57534E]">+64 27 410 8444</p>
                  </div>
                </a>
              </div>

              <div className="bg-[#F2F0E9] p-8">
                <h3 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-4">
                  What to Expect
                </h3>
                <ul className="space-y-3 text-[#57534E]">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Response within 24 hours</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Free initial consultation call</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>Tailored approach to your goals</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-[#2C5530] mt-0.5 flex-shrink-0" />
                    <span>In-person or remote sessions available</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              {submitted ? (
                <div className="bg-white border border-[#E7E5E4] p-12 text-center" data-testid="contact-success">
                  <CheckCircle className="w-16 h-16 text-[#2C5530] mx-auto mb-6" />
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-4">
                    Message Received!
                  </h3>
                  <p className="text-[#57534E] mb-6">
                    Thank you for reaching out. I'll get back to you as soon as possible, usually within 24 hours.
                  </p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setFormData({ name: '', email: '', phone: '', service_interest: '', message: '' });
                    }}
                    className="text-[#2C5530] font-semibold hover:underline"
                    data-testid="send-another-btn"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="bg-white border border-[#E7E5E4] p-8 md:p-10" data-testid="contact-form">
                  <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A] mb-6">
                    Send a Message
                  </h3>

                  <div className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Phone (optional)
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="service_interest" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        I'm interested in...
                      </label>
                      <select
                        id="service_interest"
                        name="service_interest"
                        value={formData.service_interest}
                        onChange={handleChange}
                        className="w-full"
                        data-testid="contact-service-select"
                      >
                        <option value="">Select an option</option>
                        <option value="1-on-1 Coaching">1-on-1 Coaching</option>
                        <option value="Online Sessions">Online Sessions</option>
                        <option value="Group Workshops">Group Workshops</option>
                        <option value="Coach Account">Coach Account</option>
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#1A1A1A] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="w-full resize-none"
                        placeholder="Tell me about your goals and what you'd like to work on..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full bg-[#2C5530] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        'Sending...'
                      ) : (
                        <>
                          Send Message
                          <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
